/** Where a search looks: one environment, every environment of one customer, or everywhere. */
export interface SearchScope {
    customer?: string;
    environment?: string;
}

export interface ParsedSearch extends SearchScope {
    /** What is left to search for once the customer and environment words are taken out. */
    text: string;
    /** Whether the text itself named the scope. */
    typed: boolean;
}

/**
 * Takes the customer and environment out of what was typed: "26700642 orbisk prod" searches 26700642 in orbisk prod,
 * "orbisk 26700642" in every environment of orbisk. Words match a customer or one of its environments by name,
 * ignoring case; an environment word without a customer counts only when one customer has it.
 */
export const parseSearch = (typed: string, environments: { customer: string; environment: string }[]): ParsedSearch => {
    const words = typed.trim().split(/\s+/).filter(Boolean);
    const lower = (v: string) => v.toLowerCase();
    const customers = new Map(environments.map((e) => [lower(e.customer), e.customer]));

    const customerWord = words.find((w) => customers.has(lower(w)));
    const customer = customerWord ? customers.get(lower(customerWord)) : undefined;

    // A search for the customer's own name only is a search for that name, not a scope without anything to find.
    if (customer && words.length === 1) return { text: typed.trim(), typed: false };

    const candidates = environments.filter((e) => !customer || lower(e.customer) === lower(customer));
    const environmentWord = words.find((w) => w !== customerWord && candidates.some((e) => lower(e.environment) === lower(w)));
    const owners = environmentWord ? candidates.filter((e) => lower(e.environment) === lower(environmentWord)) : [];
    const environmentOf = owners.length === 1 || customer ? owners[0] : undefined;

    const used = new Set([customerWord, environmentOf ? environmentWord : undefined].filter(Boolean));
    const text = words.filter((w) => !used.has(w)).join(' ');

    if (!text) return { text: typed.trim(), typed: false };

    return {
        text,
        customer: environmentOf?.customer ?? customer,
        environment: environmentOf?.environment,
        typed: used.size > 0,
    };
};

/** "orbisk prod", "orbisk", "alle omgevingen". */
export const scopeLabel = (scope: SearchScope) =>
    scope.customer ? (scope.environment ? `${scope.customer} ${scope.environment}` : scope.customer) : 'alle omgevingen';
